import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { setPresentationActive } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePresentation } from "@/context/PresentationContext";

const GuidedTourContext = createContext(null);

export function GuidedTourProvider({ children }) {
  const { user } = useAuth();
  const { setMode } = usePresentation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language === "es" ? "es" : "en";

  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const active = !!session && session.status === "in_progress";

  // Agents use the live, element-level Command Center spotlight tour. Do not surface
  // the isolated presentation-sandbox entry (or its "not prepared" warning) to them.
  useEffect(() => {
    if (!user || user === false || user.role === "vendor") return;
    if (user.role === "agent") {
      setSession(null);
      setUnavailable(false);
      return;
    }
    api.get(`/copilot/presentation?lang=${lang}`).then(({ data }) => setSession(data)).catch(() => {});
  }, [user, lang]);

  const goToStepRoute = useCallback((state) => {
    const s = state?.steps?.[state.step];
    if (s?.route && window.location.pathname !== s.route) navigate(s.route);
  }, [navigate]);

  const enterDemo = useCallback(() => {
    setPresentationActive(true);
    setMode("demo");
  }, [setMode]);

  const exitDemo = useCallback((returnRoute) => {
    setPresentationActive(false);
    setMode("live");
    if (returnRoute) navigate(returnRoute);
  }, [setMode, navigate]);

  const start = useCallback(async () => {
    if (busy) return;
    if (user?.role === "agent") {
      window.dispatchEvent(new CustomEvent("fortunate:start-agent-tour"));
      return;
    }
    setBusy(true); setUnavailable(false);
    try {
      const returnRoute = window.location.pathname;
      const { data } = await api.post("/copilot/presentation/start", { return_route: returnRoute, lang });
      if (!data.demo_available) { setUnavailable(true); setSession(data); return; }
      enterDemo();
      setSession(data);
      goToStepRoute(data);
    } finally { setBusy(false); }
  }, [busy, user?.role, lang, enterDemo, goToStepRoute]);

  const nav = useCallback(async (op, step) => {
    if (busy || user?.role === "agent") return;
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/presentation/nav", { op, step, lang });
      setSession(data);
      if (op === "end") { exitDemo(data.return_route); return; }
      if (data.status === "in_progress") goToStepRoute(data);
      return data;
    } finally { setBusy(false); }
  }, [busy, user?.role, lang, exitDemo, goToStepRoute]);

  const goNext = useCallback(() => nav("next"), [nav]);
  const goBack = useCallback(() => nav("back"), [nav]);
  const goto = useCallback((i) => nav("goto", i), [nav]);
  const end = useCallback(() => nav("end"), [nav]);
  const restart = useCallback(async () => {
    if (user?.role === "agent") {
      window.dispatchEvent(new CustomEvent("fortunate:start-agent-tour"));
      return;
    }
    enterDemo();
    const data = await nav("restart");
    if (data) goToStepRoute(data);
  }, [user?.role, enterDemo, nav, goToStepRoute]);

  const resume = useCallback(async () => {
    if (user?.role === "agent") {
      window.dispatchEvent(new CustomEvent("fortunate:start-agent-tour"));
      return;
    }
    enterDemo();
    const data = await nav("resume");
    if (data) goToStepRoute(data);
  }, [user?.role, enterDemo, nav, goToStepRoute]);

  const close = useCallback(async () => {
    if (busy || user?.role === "agent") return;
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/presentation/nav", { op: "pause", lang });
      setSession(data);
      exitDemo(data.return_route);
    } finally { setBusy(false); }
  }, [busy, user?.role, lang, exitDemo]);

  const setNarration = useCallback(async (enabled) => {
    if (user?.role === "agent") return;
    setSession((s) => (s ? { ...s, narration_enabled: enabled } : s));
    try { await api.put("/copilot/presentation/narration", { enabled }); } catch (_) {}
  }, [user?.role]);

  return (
    <GuidedTourContext.Provider value={{
      session, active, busy, unavailable, setUnavailable,
      start, goNext, goBack, goto, end, restart, resume, close, setNarration,
    }}>
      {children}
    </GuidedTourContext.Provider>
  );
}

export function useGuidedTour() {
  return useContext(GuidedTourContext) || {};
}
